<template>
  <div class="pricing-page">
    <div class="pricing-page-content-wrapper">
      <div class="pricing-page-title">
        {{ $t('Pricing.common.subscriptions') }}
      </div>
      <div class="pricing-page-header mb-10">
        {{ $t('Pricing.common.choosePlan') }}
      </div>
      <div v-if="!isGuestUser && librarySetPrices" class="mb-6">
        <div class="section-subtitle mb-2">
          {{ $t('Settings.sub.plans.current') }}
        </div>
        <CurrentPlan :prices="librarySetPrices" />
      </div>
      <div class="pricing-container">
        <div
          v-for="price in librarySetPrices"
          :key="price.key"
          class="pricing-item"
        >
          <div class="item-title">{{ price.title }}</div>
          <div class="item-description">
            <div>{{ price.groupTypeTitle }}</div>
            <div v-if="price.description">
              {{ price.description }}
            </div>
            <div
              v-if="getPromoCode && price.formattedPriceWithDiscount"
              class="discount-label"
            >
              {{
                $t('Pricing.common.annual.discount', {
                  percent: getPromoCode.percentOff
                })
              }}
            </div>
          </div>
          <div
            class="item-price"
            :class="{ 'price-before-discount': hasDiscount(price) }"
          >
            <div class="price-text">
              $<span class="value-text">{{ price.formattedPrice }}</span>
            </div>
          </div>
          <div v-if="hasDiscount(price)" class="item-price">
            <div class="price-text">
              $<span class="value-text">{{
                price.formattedPriceWithDiscount
              }}</span>
            </div>
          </div>
          <SubscribeButton
            class="item-link"
            :price="price"
            color="primary"
            :show-for-subscribed="true"
            :disabled="!isOnline || isUserFamilyGroupMember"
          />
        </div>
      </div>
      <PromoCodeBanner
        v-if="!isUserFamilyGroupMember"
        class="mt-10"
        @change="promoCodeHandler"
      />
      <div class="pricing-page-header mt-16">
        {{ $t('Pricing.common.whatYouGet') }}
      </div>
      <div class="features-block">
        <div class="features-item">
          <div class="pic-block">
            <v-img
              :lazy-src="featuresPic1"
              :src="featuresPic1"
              alt="features-picture"
            />
          </div>
          <p>{{ $t('Pricing.common.whatYouGet.alignedText') }}</p>
        </div>
        <div class="features-item">
          <div class="pic-block">
            <v-img
              :lazy-src="featuresPic2"
              :src="featuresPic2"
              alt="features-picture"
            />
          </div>
          <p>{{ $t('Pricing.common.whatYouGet.assessment') }}</p>
        </div>
        <div class="features-item">
          <div class="pic-block">
            <v-img
              :lazy-src="featuresPic3"
              :src="featuresPic3"
              alt="features-picture"
            />
          </div>
          <p>{{ $t('Pricing.common.whatYouGet.activityReports') }}</p>
        </div>
        <div class="features-item">
          <div class="pic-block">
            <v-img
              :lazy-src="featuresPic4"
              :src="featuresPic4"
              alt="features-picture"
            />
          </div>
          <p>{{ $t('Pricing.common.whatYouGet.library') }}</p>
        </div>
      </div>
    </div>
    <AppFooter />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { localize as $t } from '@/i18n';
import SubscribeButton from '@/components/base/SubscribeButton/SubscribeButton.vue';
import AppFooter from '@/components/views/AppFooter/AppFooter.vue';
import { GroupTypesEnum, TypesEnum } from '@shared/enums/SubscriptionTypesEnum';
import ManagePublicationsStatesEnum from '@/enums/ManagePublicationsStatesEnum';
import Utils from '@/services/utils/Utils';

import LoggerFactory from '@/services/utils/LoggerFactory';
const logger = LoggerFactory.getLogger('Pricing.vue');

import CurrentPlan from '@/components/views/CurrentPlan/CurrentPlan.vue';
import PromoCodeBanner from '@/components/views/PromoCodeBanner/PromoCodeBanner.vue';
import featuresPic1 from '@/components/views/AboutView/ffa/img/features-pic1.svg';
import featuresPic2 from '@/components/views/AboutView/ffa/img/features-pic2.svg';
import featuresPic3 from '@/components/views/AboutView/ffa/img/features-pic3.svg';
import featuresPic4 from '@/components/views/AboutView/ffa/img/features-pic4.svg';

// TODO think about move to factory and reuse in Planes.vue
class PriceItem {
  constructor(price) {
    const isFamily = price.groupType === GroupTypesEnum.FAMILY;
    const isMonthly = price.type === TypesEnum.MONTHLY;
    const title = isFamily
      ? $t('Pricing.common.family')
      : isMonthly
      ? $t('Pricing.common.monthly')
      : $t('Pricing.common.annual');
    const groupTypeTitle = isFamily
      ? $t('Pricing.common.family.description')
      : $t('Pricing.common.individual.description');
    return {
      title,
      groupTypeTitle,
      type: price.type,
      groupType: price.groupType,
      price: price.price,
      key: price.type + price.groupType,
      formattedPrice: Utils.getFormattedPrice(price.oldPrice ?? price.price),
      priceWithDiscount: price.oldPrice ? price.price : null,
      formattedPriceWithDiscount: price.oldPrice
        ? Utils.getFormattedPrice(price.price)
        : null,
      description: isFamily
        ? $t('Pricing.common.family.amountDescription')
        : null
    };
  }
}

export default {
  name: 'PricingView',
  components: {
    SubscribeButton,
    AppFooter,
    CurrentPlan,
    PromoCodeBanner
  },
  data() {
    return {
      prices: null,
      featuresPic1,
      featuresPic2,
      featuresPic3,
      featuresPic4
    };
  },
  computed: {
    ...mapGetters('ContextStore', ['isOnline']),
    ...mapGetters('LibraryStore', ['getLibrarySet']),
    ...mapGetters('PaymentsStore', ['getPromoCode']),
    ...mapGetters('UserStore', [
      'isUserFamilyGroupMember',
      'isGuestUser',
      'isAuthenticated'
    ]),
    librarySetPrices() {
      const prices = this.prices ?? this.getLibrarySet?.prices;
      if (!prices?.length) {
        return null;
      }
      return prices.map(p => new PriceItem(p));
    },
    librarySetId() {
      return this.getLibrarySet?.id;
    },
    promoCode() {
      return this.getPromoCode?.code;
    }
  },
  watch: {
    async isAuthenticated(val) {
      if (val) {
        await this.loadPricingData();
      }
    },
    async promoCode(val) {
      if (val && this.isAuthenticated) {
        await this.loadPricingData();
      }
    }
  },
  async mounted() {
    if (this.isAuthenticated) {
      await this.loadPricingData();
    }
  },
  methods: {
    ...mapActions('PaymentsStore', ['calcDiscount']),
    async loadPricingData() {
      await this.$store.dispatch('LibraryStore/initState', {
        managePublicationsState: ManagePublicationsStatesEnum.PRICING
      });
      if (this.getPromoCode) {
        await this.calculateDiscount();
      }
    },
    hasDiscount(price) {
      return Number.isFinite(price.priceWithDiscount);
    },
    async promoCodeHandler() {
      await this.calculateDiscount();
    },
    async calculateDiscount() {
      let discount;
      if (!this.getPromoCode?.code) {
        this.prices = this.librarySetPrices;
      }
      try {
        discount = await this.calcDiscount({
          setId: this.librarySetId,
          promoCode: this.getPromoCode.code
        });
        this.prices = discount?.newPrices;
      } catch (e) {
        logger.error(`Error on calculating discount: ${JSON.stringify(e)}`);
        this.prices = null;
      }
    }
  }
};
</script>

<style src="./Pricing.less" lang="less"></style>
